import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpTrayIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Button } from '../../../../components/Button';
import { Text } from '../../../../components/Text';
import Loading from '../../../../components/Loading';
import Pagination from '../../../../components/Pagination';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../../components/Table';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../../components/Dialog';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { useAiReportDatasets } from '../../../../hooks/useAiReportDataset';
import { AiReportDataset, AiReportDatasetColumn, AiReportDatasetRow, DATASET_MIN_COLUMNS } from '../../../../models/AiReportDataset';
import ColumnBuilder from '../ColumnBuilder';

const EditDatasetPage = () => {
    const { id } = useParams<{ id: string }>();
    const datasetId = Number(id);
    const { fetchDatasetById, updateDataset, updateRow, deleteRow, importRows, bulkAddRows } = useAiReportDatasets();

    const [dataset, setDataset] = useState<AiReportDataset | null>(null);
    const [rows, setRows] = useState<AiReportDatasetRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [columns, setColumns] = useState<AiReportDatasetColumn[]>([]);
    const [referredKey, setReferredKey] = useState('');
    const [savingStructure, setSavingStructure] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(10);

    const [rowDialogOpen, setRowDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState<AiReportDatasetRow | null>(null);
    const [rowDraft, setRowDraft] = useState<Record<string, string>>({});
    const [savingRow, setSavingRow] = useState(false);

    const [deleteRowId, setDeleteRowId] = useState<number | null>(null);
    const [deletingRow, setDeletingRow] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const importInputRef = useRef<HTMLInputElement>(null);

    const [addRowsOpen, setAddRowsOpen] = useState(false);
    const [addRowsDraft, setAddRowsDraft] = useState<Record<string, string>[]>([]);
    const [savingRows, setSavingRows] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const fetched = await fetchDatasetById(datasetId);
            setDataset(fetched);
            setRows(fetched.rows || []);
            setName(fetched.name);
            setColumns(fetched.columns);
            setReferredKey(fetched.referred_column_key);
        } catch (err) {
            showErrorToast('Failed to fetch data set');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasetId]);

    const handleSaveStructure = async () => {
        if (!name.trim()) {
            showErrorToast('Name is required');
            return;
        }
        const labeledColumns = columns.filter((c) => c.label.trim());
        if (labeledColumns.length < DATASET_MIN_COLUMNS) {
            showErrorToast(`Keep at least ${DATASET_MIN_COLUMNS} named columns`);
            return;
        }
        if (!labeledColumns.some((c) => c.key === referredKey)) {
            showErrorToast('Pick a referred column');
            return;
        }
        setSavingStructure(true);
        try {
            const updated = await updateDataset(datasetId, { name: name.trim(), columns: labeledColumns, referredColumnKey: referredKey });
            setDataset((prev) => (prev ? { ...prev, ...updated, rows: prev.rows } : updated));
            setColumns(labeledColumns);
            showSuccessToast('Data set structure saved');
        } catch (err: any) {
            showErrorToast(err?.response?.data?.error || 'Failed to save data set');
        } finally {
            setSavingStructure(false);
        }
    };

    const openEditRow = (row: AiReportDatasetRow) => {
        setEditingRow(row);
        setRowDraft(Object.fromEntries(columns.map((c) => [c.key, row.row_data[c.key] || ''])));
        setRowDialogOpen(true);
    };

    const handleSaveRow = async () => {
        if (!editingRow) return;
        const referredColumn = columns.find((c) => c.key === referredKey);
        if (!rowDraft[referredKey]?.trim()) {
            showErrorToast(`"${referredColumn?.label || 'Referred column'}" is required`);
            return;
        }
        setSavingRow(true);
        try {
            const updated = await updateRow(datasetId, editingRow.id, rowDraft);
            setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            showSuccessToast('Row updated');
            setRowDialogOpen(false);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.error || 'Failed to save row');
        } finally {
            setSavingRow(false);
        }
    };

    const emptyDraftRow = () => Object.fromEntries(columns.map((c) => [c.key, '']));

    const openAddRows = () => {
        setAddRowsDraft([emptyDraftRow(), emptyDraftRow(), emptyDraftRow()]);
        setAddRowsOpen(true);
    };

    const updateDraftCell = (rowIdx: number, colKey: string, value: string) => {
        setAddRowsDraft((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, [colKey]: value } : r)));
    };

    const addDraftRow = () => setAddRowsDraft((prev) => [...prev, emptyDraftRow()]);

    const removeDraftRow = (rowIdx: number) => setAddRowsDraft((prev) => prev.filter((_, i) => i !== rowIdx));

    const handleSaveRows = async () => {
        const filled = addRowsDraft.filter((r) => (r[referredKey] || '').trim());
        if (filled.length === 0) {
            showErrorToast(`Fill in "${columns.find((c) => c.key === referredKey)?.label || 'the referred column'}" for at least one row`);
            return;
        }
        setSavingRows(true);
        try {
            const result = await bulkAddRows(datasetId, filled);
            showSuccessToast(`${result.created} row${result.created === 1 ? '' : 's'} added${result.updated > 0 ? `, ${result.updated} updated` : ''}`);
            setAddRowsOpen(false);
            await load();
        } catch (err: any) {
            showErrorToast(err?.response?.data?.error || 'Failed to add rows');
        } finally {
            setSavingRows(false);
        }
    };

    const handleDeleteRow = async () => {
        if (deleteRowId === null) return;
        setDeletingRow(true);
        try {
            await deleteRow(datasetId, deleteRowId);
            setRows((prev) => prev.filter((r) => r.id !== deleteRowId));
            showSuccessToast('Row deleted');
            setDeleteRowId(null);
        } catch (err) {
            showErrorToast('Failed to delete row');
        } finally {
            setDeletingRow(false);
        }
    };

    const handleImportClick = () => importInputRef.current?.click();

    const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setIsImporting(true);
        try {
            const result = await importRows(datasetId, file);
            const parts = [`${result.created} created`, `${result.updated} updated`];
            if (result.skippedRows.length > 0) parts.push(`${result.skippedRows.length} skipped (missing referred column)`);
            showSuccessToast(`Import complete: ${parts.join(', ')}`);
            await load();
        } catch (err: any) {
            showErrorToast(err?.response?.data?.error || 'Failed to import rows');
        } finally {
            setIsImporting(false);
        }
    };

    if (loading) return <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>;
    if (!dataset) return <p>Data set not found.</p>;

    const indexOfLast = currentPage * perPage;
    const indexOfFirst = indexOfLast - perPage;
    const currentRows = rows.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(rows.length / perPage);

    return (
        <div className="mx-auto max-w-5xl">
            <Link
                to="/gp/ai-report-helper/datasets"
                className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
            >
                <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                Back to Data Sets
            </Link>
            <Heading className="mt-4 mb-5">Edit Data Set</Heading>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Name</h2>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Name</Subheading>
                            <div className="mt-2">
                                <Input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Columns</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Changing the referred column recomputes every row's match value.
                        </p>
                    </div>
                    <div className="max-w-2xl md:col-span-2">
                        <ColumnBuilder
                            columns={columns}
                            referredKey={referredKey}
                            onColumnsChange={setColumns}
                            onReferredKeyChange={setReferredKey}
                        />
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleSaveStructure} disabled={savingStructure}>
                                {savingStructure ? <Loading /> : 'Save structure'}
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="mt-6 p-6">
                <div>
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-foreground">Rows</h2>
                            <Text className="mt-1 text-sm">{rows.length} row{rows.length === 1 ? '' : 's'}</Text>
                        </div>
                        <div className="flex gap-3">
                            <input
                                ref={importInputRef}
                                type="file"
                                accept=".xlsx"
                                className="hidden"
                                onChange={handleImportFileChange}
                            />
                            <Button outline onClick={handleImportClick} disabled={isImporting}>
                                <ArrowUpTrayIcon />
                                {isImporting ? 'Importing...' : 'Upload Excel'}
                            </Button>
                            <Button onClick={openAddRows}>+ Add rows</Button>
                        </div>
                    </div>
                    <p className="mt-2 text-xs text-zinc-500">
                        Upload Excel columns: {columns.map((c) => c.label).join(', ')} (a value in the referred
                        column, "{columns.find((c) => c.key === referredKey)?.label}", is required; a matching
                        existing row is updated instead of duplicated).
                    </p>

                    <div className="mt-6">
                        {rows.length === 0 ? (
                            <p className="py-16 text-center">No rows yet.</p>
                        ) : (
                            <Table striped>
                                <TableHead>
                                    <TableRow>
                                        {columns.map((c) => <TableHeader key={c.key}>{c.label}</TableHeader>)}
                                        <TableHeader className="text-right">Actions</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentRows.map((row) => (
                                        <TableRow key={row.id}>
                                            {columns.map((c) => <TableCell key={c.key}>{row.row_data[c.key] || ''}</TableCell>)}
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-3">
                                                    <button onClick={() => openEditRow(row)} aria-label="Edit row" className="text-zinc-400 hover:text-zinc-950 dark:hover:text-white">
                                                        <PencilIcon className="size-4" />
                                                    </button>
                                                    <button onClick={() => setDeleteRowId(row.id)} aria-label="Delete row" className="text-zinc-400 hover:text-red-600">
                                                        <TrashIcon className="size-4" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalResult={rows.length}
                        dataName='Rows'
                    />
                </div>
            </Card>

            <Dialog open={rowDialogOpen} onClose={() => setRowDialogOpen(false)}>
                <DialogTitle>Edit Row</DialogTitle>
                <DialogBody>
                    <div className="space-y-4">
                        {columns.map((c) => (
                            <div key={c.key}>
                                <Subheading>
                                    {c.label}{c.key === referredKey ? ' (referred)' : ''}
                                </Subheading>
                                <div className="mt-1">
                                    <Input
                                        aria-label={c.label}
                                        value={rowDraft[c.key] || ''}
                                        onChange={(e) => setRowDraft((prev) => ({ ...prev, [c.key]: e.target.value }))}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setRowDialogOpen(false)} disabled={savingRow}>Cancel</Button>
                    <Button onClick={handleSaveRow} disabled={savingRow}>{savingRow ? <Loading /> : 'Save'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addRowsOpen} onClose={() => setAddRowsOpen(false)} size="5xl">
                <DialogTitle>Add Rows</DialogTitle>
                <DialogBody>
                    <Text className="mb-3 text-sm">
                        Fill in as many rows as you like, spreadsheet-style. Rows without a
                        "{columns.find((c) => c.key === referredKey)?.label}" value are ignored.
                    </Text>
                    <div className="max-h-[60vh] overflow-auto rounded-lg border border-zinc-950/10 dark:border-white/10">
                        <table className="min-w-full border-collapse text-sm">
                            <thead className="sticky top-0 z-10 bg-zinc-100 dark:bg-zinc-800">
                                <tr>
                                    <th className="w-10 border border-zinc-950/10 px-2 py-1.5 text-center text-xs text-zinc-500 dark:border-white/10">#</th>
                                    {columns.map((c) => (
                                        <th key={c.key} className="min-w-[160px] border border-zinc-950/10 px-2 py-1.5 text-left font-medium dark:border-white/10">
                                            {c.label}{c.key === referredKey && <span className="ml-1 text-violet-600">*</span>}
                                        </th>
                                    ))}
                                    <th className="w-10 border border-zinc-950/10 dark:border-white/10" />
                                </tr>
                            </thead>
                            <tbody>
                                {addRowsDraft.map((row, rowIdx) => (
                                    <tr key={rowIdx}>
                                        <td className="border border-zinc-950/10 px-2 py-0.5 text-center text-xs text-zinc-400 dark:border-white/10">
                                            {rowIdx + 1}
                                        </td>
                                        {columns.map((c) => (
                                            <td key={c.key} className="border border-zinc-950/10 p-0 dark:border-white/10">
                                                <input
                                                    aria-label={`Row ${rowIdx + 1} ${c.label}`}
                                                    value={row[c.key] || ''}
                                                    onChange={(e) => updateDraftCell(rowIdx, c.key, e.target.value)}
                                                    className="w-full bg-transparent px-2 py-1.5 text-sm text-zinc-950 focus:bg-blue-50 focus:outline-none dark:text-white dark:focus:bg-blue-950/40"
                                                />
                                            </td>
                                        ))}
                                        <td className="border border-zinc-950/10 text-center dark:border-white/10">
                                            <button
                                                type="button"
                                                onClick={() => removeDraftRow(rowIdx)}
                                                aria-label="Remove row"
                                                className="text-zinc-400 hover:text-red-600"
                                            >
                                                <TrashIcon className="size-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Button outline type="button" onClick={addDraftRow} className="mt-3">
                        <PlusIcon />
                        Add row
                    </Button>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setAddRowsOpen(false)} disabled={savingRows}>Cancel</Button>
                    <Button onClick={handleSaveRows} disabled={savingRows}>{savingRows ? <Loading /> : 'Save'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteRowId !== null} onClose={() => setDeleteRowId(null)}>
                <DialogTitle>Delete Row</DialogTitle>
                <DialogDescription>Are you sure you want to delete this row? This action cannot be undone.</DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setDeleteRowId(null)} disabled={deletingRow}>Cancel</Button>
                    <Button onClick={handleDeleteRow} disabled={deletingRow}>{deletingRow ? <Loading /> : 'Delete'}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default EditDatasetPage;
